import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { htmlToExcerpt } from "../utils/sanitizeHtml";
import HiringForm from "./HiringForm";
import FitCoverImage from "./shared/FitCoverImage";

const API_URL = getApiBaseUrl();

const btnBase = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  color: "#0f172a",
};

/**
 * Admin panel: list, create, and moderate all hiring posts.
 */
export default function AdminHiringPanel({ user, onRefresh }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setStatus("");
    try {
      const res = await fetch(`${API_URL}/get_hiring_posts.php?limit=50`);
      const data = await res.json();
      if (!data?.success) {
        setStatus(data?.message || "Failed to load hiring posts.");
        setPosts([]);
        return;
      }
      const list = data.posts || [];
      setPosts(list);
      setActiveId((prev) => {
        if (prev && list.some((p) => String(p.id) === String(prev))) return prev;
        return list[0]?.id ?? null;
      });
    } catch (err) {
      setStatus(err?.message || "Network error loading hiring posts.");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((p) => {
      const hay = [
        p.title,
        p.hiring_company,
        p.hiring_location,
        p.username,
        p.hiring_description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [posts, query]);

  const activePost = useMemo(
    () => filtered.find((p) => String(p.id) === String(activeId)) || null,
    [filtered, activeId]
  );

  const relativeTime = (dateString) => {
    if (!dateString) return "";
    const created = new Date(String(dateString).replace(" ", "T"));
    const diffSec = Math.floor((Date.now() - created.getTime()) / 1000);
    if (Number.isNaN(diffSec)) return dateString;
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} mins ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hrs ago`;
    return `${Math.floor(diffSec / 86400)} days ago`;
  };

  const handleDelete = async (post) => {
    if (!post?.id) return;
    const label = post.title || `post #${post.id}`;
    if (
      !window.confirm(
        `Delete hiring post “${label}”? All applications and resumes for this job will also be permanently deleted.`
      )
    ) {
      return;
    }
    setBusyId(post.id);
    setStatus("Deleting…");
    try {
      const formData = new FormData();
      formData.append("post_id", String(post.id));
      const res = await fetch(`${API_URL}/delete_post.php`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!data?.success) {
        setStatus(data?.message || "Failed to delete post.");
        return;
      }
      setStatus("Hiring post deleted.");
      onRefresh?.();
      await loadPosts();
    } catch (err) {
      setStatus(err?.message || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ color: "#0f172a" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Hiring posts</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            View, create, and moderate all job ads.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          style={{
            ...btnBase,
            background: "#0f766e",
            borderColor: "#0f766e",
            color: "#fff",
          }}
        >
          + New hiring post
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input
          type="search"
          placeholder="Search title, company, location, poster…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: 1,
            minWidth: 180,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            fontSize: 14,
            outline: "none",
          }}
        />
        <button type="button" onClick={loadPosts} style={btnBase}>
          Refresh
        </button>
      </div>

      {status && (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#0369a1" }}>{status}</p>
      )}

      {loading ? (
        <p style={{ color: "#64748b", fontSize: 14 }}>Loading hiring posts…</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: "#64748b", fontSize: 14 }}>
          {posts.length === 0
            ? "No hiring posts yet. Create one to get started."
            : "No posts match your search."}
        </p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
            gap: 14,
            alignItems: "start",
          }}
          className="admin-hiring-grid"
        >
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              maxHeight: "60vh",
              overflowY: "auto",
              background: "#f8fafc",
            }}
          >
            {filtered.map((post) => {
              const selected = String(post.id) === String(activeId);
              return (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => setActiveId(post.id)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    border: "none",
                    borderBottom: "1px solid #e2e8f0",
                    background: selected ? "#ccfbf1" : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: 4,
                      lineHeight: 1.3,
                    }}
                  >
                    {post.title || "Untitled"}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569" }}>
                    {[post.hiring_company, post.hiring_location]
                      .filter(Boolean)
                      .join(" · ") || "No company / location"}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    {[post.username ? `@${post.username}` : null, relativeTime(post.created_at)]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 16,
              background: "#fff",
              minHeight: 220,
            }}
          >
            {!activePost ? (
              <p style={{ color: "#64748b", fontSize: 14 }}>Select a hiring post.</p>
            ) : (
              <>
                <FitCoverImage
                  src={activePost.cover_image}
                  maxHeight={220}
                  radius={10}
                  marginBottom={12}
                />
                <div
                  style={{
                    display: "inline-block",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: "#0f766e",
                    background: "#ccfbf1",
                    padding: "3px 8px",
                    borderRadius: 4,
                    marginBottom: 8,
                  }}
                >
                  Hiring
                </div>
                <h3 style={{ margin: "0 0 6px", fontSize: 18, lineHeight: 1.3 }}>
                  {activePost.title}
                </h3>
                <p style={{ margin: "0 0 6px", fontSize: 13, color: "#475569" }}>
                  {[activePost.hiring_company, activePost.hiring_location]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 12,
                    color:
                      activePost.hiring_closed || activePost.hiring_accepting === false
                        ? "#b91c1c"
                        : "#64748b",
                  }}
                >
                  {activePost.hiring_closed || activePost.hiring_accepting === false
                    ? "Applications closed"
                    : activePost.hiring_deadline
                      ? `Apply by ${activePost.hiring_deadline}`
                      : "No deadline · open until closed"}
                </p>
                <p style={{ margin: "0 0 12px", fontSize: 12, color: "#94a3b8" }}>
                  Posted by {activePost.username || "unknown"} ·{" "}
                  {relativeTime(activePost.created_at)}
                </p>
                <p
                  style={{
                    margin: "0 0 16px",
                    fontSize: 14,
                    color: "#475569",
                    lineHeight: 1.45,
                  }}
                >
                  {htmlToExcerpt(
                    activePost.hiring_description ||
                      activePost.text ||
                      activePost.content ||
                      "",
                    220
                  )}
                </p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <Link
                    to={`/home/hiring/${activePost.id}`}
                    style={{
                      ...btnBase,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    Open page
                  </Link>
                  <Link
                    to={`/hiring/applications?post_id=${activePost.id}`}
                    style={{
                      ...btnBase,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      background: "#f0fdfa",
                      borderColor: "#99f6e4",
                      color: "#0f766e",
                    }}
                  >
                    Applications
                  </Link>
                  {activePost.hiring_apply_url && (
                    <a
                      href={activePost.hiring_apply_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...btnBase,
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      External apply
                    </a>
                  )}
                  <button
                    type="button"
                    disabled={busyId === activePost.id}
                    onClick={() => handleDelete(activePost)}
                    style={{
                      ...btnBase,
                      background: "#fef2f2",
                      borderColor: "#fecaca",
                      color: "#b91c1c",
                      opacity: busyId === activePost.id ? 0.6 : 1,
                    }}
                  >
                    {busyId === activePost.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 700px) {
          .admin-hiring-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {showCreate && (
        <HiringForm
          user={user}
          onClose={() => setShowCreate(false)}
          onRefresh={() => {
            setStatus("Hiring post published.");
            onRefresh?.();
            loadPosts();
          }}
        />
      )}
    </div>
  );
}
